import {
  PickerFormikField,
  InputButton,
  FormLabelRAC,
  LinkButton,
  SectionMessage,
} from "@certn/cascadia-react";
import { Text, ActionList, Stack, ActionListItem } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { css } from "@emotion/css";
import { useFormikContext } from "formik";

type PackageSelectorProps = {
  packagesData: Array<{ id: string; name: string; has_rubric?: boolean }>;
  isLoading: boolean;
};

export function PackageSelector({
  packagesData,
  isLoading,
}: PackageSelectorProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [packagesWithRubrics, setPackagesWithRubrics] = useState<string[]>([]);
  const { values, setFieldValue } = useFormikContext<{
    packages: string[];
  }>();

  return (
    <>
      <Stack gap="space-2">
        <Text style="global/heading-medium">{t("Apply to packages")}</Text>
        <Text style="global/body-secondary">
          {t(
            "Choose which packages to apply this rubric to. Packages already using a rubric are hidden from the list."
          )}
        </Text>
      </Stack>
      <PickerFormikField
        showActionBar
        name={"packages"}
        testID={"package"}
        triggerRef={triggerRef as React.RefObject<HTMLDivElement>}
        label={t("Select packages (optional)")}
        maxWidth="560px"
        items={
          packagesData as {
            id: string;
            name: string;
          }[]
        }
        isLoading={isLoading}
        trigger={(props) => {
          return (
            <div ref={triggerRef} className={styles.triggerWrapper}>
              <FormLabelRAC isRequired>
                {t("Select packages (optional)")}
              </FormLabelRAC>
              <InputButton onToggle={props?.onToggle} rightIcon="expand_more">
                <Text
                  style="input/text-field"
                  color={
                    props?.selectedItems?.length < 1
                      ? "text-placeholder"
                      : "text-base"
                  }
                >
                  {t("Select package(s)")}
                </Text>
              </InputButton>
            </div>
          );
        }}
        selectionMode="multiple"
        onSelectionChange={(value) => {
          const packagesWithRubrics = Array.from(value as Set<string>)
            .map((id) => packagesData?.find((d: { id: string }) => d.id === id))
            .filter((pkg) => pkg?.has_rubric)
            .map((pkg) => pkg?.name);

          setPackagesWithRubrics(packagesWithRubrics as string[]);
          setFieldValue("packages", Array.from(value as Set<string>));
        }}
      />
      {values.packages.length > 0 && (
        <Stack gap="space-2">
          <Text style="global/heading-medium">
            {t("Selected packages")} ({values.packages.length})
          </Text>
          <ActionList>
            {values.packages?.map((id: string) => {
              const packageName = packagesData?.find(
                (d: { id: string }) => d.id === id
              )?.name;

              return (
                <ActionListItem
                  key={id}
                  variation="1-line"
                  title={packageName}
                  trailing={
                    <LinkButton
                      testID={""}
                      linkStyle="monochrome"
                      onPress={() => {
                        setFieldValue(
                          "packages",
                          values.packages.filter((p: string) => p !== id)
                        );
                      }}
                    >
                      {t("Remove")}
                    </LinkButton>
                  }
                />
              );
            })}
          </ActionList>
          <br />
          {packagesWithRubrics.length > 0 && (
            <SectionMessage
              type="warning"
              icon="warning"
              sectionTitle={t("Override warning")}
            >
              {packagesWithRubrics.map((name) => (
                <Text key={name}>{name + " "}</Text>
              ))}
              {t(
                "already has a rubric applied, continuing to create the rubric will override the current rubric connected"
              )}
            </SectionMessage>
          )}
        </Stack>
      )}
    </>
  );
}

const styles = {
  triggerWrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
};
